import React, { useRef, useState } from 'react'
import { useActivityContext } from '../Context'
import { useError, useMutation } from '@ztechapps/common.hook'
import { ShowIf } from '@ztechapps/webui.util'
import { ButtonGroup, Stack } from 'react-bootstrap'
import { RichTextInput } from '@ztechapps/webui.input'
import { BusyButton } from '@ztechapps/webui.button'
import { AddNewActivityButton } from '../AddNewActivityButton'
import { useMention } from './useMention'
import isEmpty from 'lodash/isEmpty'
import { mutationCreateRemarksActivityMention } from '../graphql'
import { Form } from '@ztechapps/webui.form'
import type { UserOption } from '../constants'

export const AddMentionInput = () => {
  const [isLoading, setIsLoading] = useState(false)

  const chatInputRef = useRef(null)

  // Create a ref to hold the latest state value
  const activityRef = useRef('')

  const { documentId, disabled, showInput, setShowInput, reset, activities, entityName, mention } = useActivityContext()

  // This mutable object persists immediately without waiting for a re-render.
  const optionsRef = useRef<UserOption[]>([])

  const [newActivity, setNewActivity] = React.useState('')

  // Update the ref whenever the state changes
  activityRef.current = newActivity

  const handleReset = () => {
    reset()
    setNewActivity('')
    setShowInput(false)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    // Use the ref's current value in the submission logic
    const currentActivity = activityRef.current

    await onAdd({ input: { activity: currentActivity, templateKey, url, parentId: documentId } })

    handleReset()
    setIsLoading(false)
  }

  const { trigger, getOptions, templateKey, url, inputProps } = mention
  // Pass the updated handleSubmit to useMention
  const { setupAutocompleter, placeholder } = useMention({ trigger, optionsRef, getOptions, handleReset, handleSubmit })

  const addMutation = useMutation({ query: mutationCreateRemarksActivityMention(entityName) })

  const [onAdd, , { loading, error }] = addMutation ?? []

  useError(error)

  return (
    <ShowIf condition={!disabled}>
      <ShowIf condition={showInput}>
        <div style={{ position: 'relative' }}>
          <Form onSubmit={handleSubmit} onReset={handleReset} initialValues={{}}>
            <Stack gap={2}>
              <RichTextInput
                disabled={isLoading}
                editorRef={chatInputRef}
                value={newActivity}
                onChange={(e) => {
                  setNewActivity(e)
                  // The ref is updated via activityRef.current = newActivity above
                }}
                init={{
                  setup: setupAutocompleter,
                  menubar: false,
                  toolbar: false,
                  branding: false,
                  height: '100px',
                  placeholder,
                  ...inputProps,
                }}
              />
              <div>
                <ButtonGroup size="sm">
                  <BusyButton
                    type="submit"
                    disabled={!newActivity}
                    isBusy={loading}
                    text="Add"
                    icon="plus"
                    busyText="Adding..."
                    title="Add new activity"
                  />
                  <BusyButton type="reset" text="Cancel" icon="ban" title="Cancel changes" bsStyle="secondary" />
                </ButtonGroup>
              </div>
            </Stack>
          </Form>
        </div>
      </ShowIf>
      <ShowIf condition={!showInput && !isEmpty(activities)}>
        <AddNewActivityButton />
      </ShowIf>
    </ShowIf>
  )
}
